import * as React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowDown, ArrowUp, Copy, GripVertical, Trash2 } from 'lucide-react';
import type { Block } from '@pageboss/shared';
import { BlockPreview } from './block-preview';
import { CanvasHoverPill, blockOpensInspector } from './inspector/canvas-hover-pill';
import { cn } from '@/lib/utils';

interface SortableBlockProps {
  block: Block;
  selected: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onSelect: () => void;
  onChange: (nextProps: Record<string, unknown>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

export function SortableBlock({
  block,
  selected,
  canMoveUp,
  canMoveDown,
  onSelect,
  onChange,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
}: SortableBlockProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
    over,
    active,
  } = useSortable({ id: block.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Stable onChange so the memoized BlockPreview only re-renders when the
  // block's own props change — not on every keystroke elsewhere on the
  // page. The ref always points at the latest parent callback (which
  // closes over this block's id, not its index, so it stays correct
  // across reorders).
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const stableOnChange = React.useCallback(
    (nextProps: Record<string, unknown>) => onChangeRef.current(nextProps),
    [],
  );

  // Show a sage insertion indicator when this block is the current
  // drop target during an active drag — and the user isn't just
  // hovering on themselves.
  const isDropTarget =
    isOver && active !== null && active.id !== block.id && over?.id === block.id;

  function onContainerKeyDown(e: React.KeyboardEvent<HTMLLIElement>) {
    // Only respond when this block is the keyboard focus root —
    // ignore key events bubbling up from EditableText inside.
    if (e.target !== e.currentTarget) return;
    if (e.key === 'ArrowUp' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (canMoveUp) onMoveUp();
    } else if (e.key === 'ArrowDown' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (canMoveDown) onMoveDown();
    } else if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      onDelete();
    }
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      data-block-id={block.id}
      tabIndex={selected ? 0 : -1}
      onClick={onSelect}
      onFocus={onSelect}
      onKeyDown={onContainerKeyDown}
      className={cn(
        'group relative rounded-xl border bg-card p-6 transition-[border-color,box-shadow,opacity,transform]',
        selected
          ? 'border-brand-500 shadow-[0_0_0_1px_var(--color-brand-500)]'
          : 'border-border-subtle hover:border-border',
        isDragging && 'opacity-30 shadow-lg ring-2 ring-brand-500 ring-offset-2',
        isDropTarget &&
          'border-brand-500 bg-brand-50/40 shadow-[0_0_0_3px_var(--color-brand-500)]',
      )}
    >
      <BlockToolbar
        dragAttributes={attributes}
        dragListeners={listeners}
        canMoveUp={canMoveUp}
        canMoveDown={canMoveDown}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
      />
      {blockOpensInspector(block.type) ? <CanvasHoverPill /> : null}
      <BlockPreview block={block} editable onChange={stableOnChange} />
    </li>
  );
}

interface BlockToolbarProps {
  dragAttributes: ReturnType<typeof useSortable>['attributes'];
  dragListeners: ReturnType<typeof useSortable>['listeners'];
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function BlockToolbar({
  dragAttributes,
  dragListeners,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
}: BlockToolbarProps) {
  return (
    <div
      className={cn(
        'absolute right-3 top-3 flex items-center gap-px rounded-md border border-border bg-card shadow-sm',
        'opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100',
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <ToolbarButton
        label="Drag to reorder"
        {...dragAttributes}
        {...dragListeners}
        className="cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Move up"
        onClick={onMoveUp}
        disabled={!canMoveUp}
      >
        <ArrowUp className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Move down"
        onClick={onMoveDown}
        disabled={!canMoveDown}
      >
        <ArrowDown className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton label="Duplicate" onClick={onDuplicate}>
        <Copy className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton label="Delete" onClick={onDelete} variant="danger">
        <Trash2 className="h-4 w-4" />
      </ToolbarButton>
    </div>
  );
}

interface ToolbarButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  variant?: 'default' | 'danger';
}

const ToolbarButton = React.forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  function ToolbarButton({ label, variant = 'default', className, children, ...rest }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        title={label}
        className={cn(
          'flex h-11 w-11 items-center justify-center text-text-secondary first:rounded-l-md last:rounded-r-md',
          'transition-colors hover:bg-surface-sunken hover:text-text-primary',
          'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-text-secondary',
          variant === 'danger' && 'hover:bg-danger-bg hover:text-danger-fg',
          className,
        )}
        {...rest}
      >
        {children}
      </button>
    );
  },
);
